import { SmartTextData, SmartTextKey, StringSmartTextReplacer } from '../interfaces/smart-text';
import { i18n, I18nUtil } from './i18n-util';

export namespace SmartTextUtil {

  interface SmartTextDefinition {
    key: SmartTextKey;
    regEx: RegExp;
    maleMessageKey?: string;
    femaleMessageKey?: string;
    fallbackMessageKey?: string;
    getString: (data: SmartTextData, util: I18nUtil) => string;
  }

  // -------------------------------- Helper Functions (Start) ----------------------------------------
  function getLastNameFirstName(data: SmartTextData, util: I18nUtil): string {
    if (data.firstName) {
      return data.lastName + ', ' + data.firstName;
    }
    return data.lastName;
  }

  function getFirstNameLastName(data: SmartTextData, util: I18nUtil): string {
    if (data.firstName) {
      return data.firstName + ' ' + data.lastName;
    }
    return data.lastName;
  }

  function getFirstName(data: SmartTextData, util: I18nUtil): string {
    if (data.firstName) {
      return data.firstName;
    }
    return util.getText(this.fallbackMessageKey);
  }

  function getLastName(data: SmartTextData, util: I18nUtil): string {
    return data.lastName;
  }

  function getGenderString(data: SmartTextData, util: I18nUtil): string {
    let messageKey: string;
    if (data.gender === 'M') {
      messageKey = this.maleMessageKey;
    } else if (data.gender === 'F') {
      messageKey = this.femaleMessageKey;
    } else {
      // We do not have alternate pronouns yet.  For now, return the fallback string (i.e., he/she, etc.)
      messageKey = this.fallbackMessageKey;

      if (data.alternatePronouns != null) {
        let alternatePronoun = data.alternatePronouns[messageKey];
        if (alternatePronoun != null) {
          return alternatePronoun;
        }
      }
    }
    return util.getText(messageKey);
  }
  // --------------------------------- Helper Functions (End) -----------------------------------------

  const smartTextDefinitions: Array<SmartTextDefinition> = [{
      key: '<last name, first name>',
      regEx: /<last name, first name>/g,
      getString: getLastNameFirstName
    }, {
      key: '<first name last name>',
      regEx: /<first name last name>/g,
      getString: getFirstNameLastName
    }, {
      key: '<preferred name>',
      regEx: /<preferred name>/g,
      fallbackMessageKey: 'student',
      getString: getFirstName
    }, {
      key: '<first name>',
      regEx: /<first name>/g,
      fallbackMessageKey: 'student',
      getString: getFirstName
    }, {
      key: '<last name>',
      regEx: /<last name>/g,
      getString: getLastName
    }, {
      key: '<he/she>',
      regEx: /<he\/she>/g,
      maleMessageKey: 'he',
      femaleMessageKey: 'she',
      fallbackMessageKey: 'he/she',
      getString: getGenderString
    }, {
      key: '<He/She>',
      regEx: /<He\/She>/g,
      maleMessageKey: 'He',
      femaleMessageKey: 'She',
      fallbackMessageKey: 'He/She',
      getString: getGenderString
    }, {
      key: '<his/her>',
      regEx: /<his\/her>/g,
      maleMessageKey: 'his',
      femaleMessageKey: 'her',
      fallbackMessageKey: 'his/her',
      getString: getGenderString
    }, {
      key: '<His/Her>',
      regEx: /<His\/Her>/g,
      maleMessageKey: 'His',
      femaleMessageKey: 'Her',
      fallbackMessageKey: 'His/Her',
      getString: getGenderString
    }, {
      key: '<him/her>',
      regEx: /<him\/her>/g,
      maleMessageKey: 'him',
      femaleMessageKey: 'her',
      fallbackMessageKey: 'him/her',
      getString: getGenderString
    }, {
      key: '<son/daughter>',
      regEx: /<son\/daughter>/g,
      maleMessageKey: 'son',
      femaleMessageKey: 'daughter',
      fallbackMessageKey: 'son/daughter',
      getString: getGenderString
    }];

  /**
   * Generates word search regex
   * @param str - the word to transform into a regular expression word search.
   * @returns word search regex
   */
  function generateWordSearchRegex(str: string): RegExp | undefined {
    if (str) {
      // Since we are building a regular expression out of the string, we need to escape the characters match the
      // regular expression metacharacters so they do not affect the word search.
      // https://stackoverflow.com/questions/3115150/how-to-escape-regular-expression-special-characters-using-javascript
      // https://stackoverflow.com/questions/3446170/escape-string-for-use-in-javascript-regex
      let escapedString = str.replace(/[\-\[\]\/{}()*+?.\\^$|]/g, '\\$&');

      // Lookahead does not work on Safari browsers.  This regex has a better chance of matching the desired string
      // instead of the word boundary used by PowerTeacher Pro (i.e.: `\\b${escapedString}\\b`).
      return RegExp(`(?!\\B)${escapedString}(?=$|\\.|,|!|\\?|;|:|\\s)`, 'g');
    }
    return undefined;
  }

  /**
   * Generates smart text from simple string
   * @param str - the string to start from and replace known words with smart tags.
   * @param relatedCommentData - the data to associate with smart tags
   * @param [util] - the i18n utility
   * @returns smart text from simple string
   */
  function generateSmartTextFromSimpleString(str: string, relatedCommentData: SmartTextData, util: I18nUtil = i18n): string {
    if (str != null && relatedCommentData != null) {
      for (let smartTextDefinition of smartTextDefinitions) {
        // Currently, the preferred name pulls in data out of the first name.  We do not have the preferred name data available yet.
        // To build the smart text template, we do not want to use the preferred name as this will conflict with the <first name> key.
        if (smartTextDefinition.key === '<preferred name>') {
          continue;
        }

        // For female gender, we will end up with two possibilities of what smart key to use: <his/her> and <him/her>.
        // We cannot simply choose one or the other without knowing the context on how the "her" is used in the input comment.
        // This will require an enhancement where we can prompt the user to pick which one but we will need to show the user
        // the context of where her is used.  For now, we are going to simply match exactly what PTP does when generating the
        // comment template.
        if (relatedCommentData.gender === 'F' && smartTextDefinition.key === '<him/her>') {
          continue;
        }

        let wordRegex = generateWordSearchRegex(smartTextDefinition.getString(relatedCommentData, util));
        if (wordRegex != null) {
          str = str.replace(wordRegex, smartTextDefinition.key);
        }
      }
    }
    return str;
  }

  /**
   * Defaults string smart text replacer.
   * @param str - string to transform into another string with smart tags.  This string should NOT contain
   *              html code as this function may replace words with smart tags within each html tags. If
   *              you need a specialized replacer, create a custom one and pass it to the generateSmartText
   *              function.
   * @param replaceWithSmartTextFunction
   * @returns string smart text replacer
   */
  function defaultStringSmartTextReplacer(str: string, replaceWithSmartTextFunction: (str: string) => string): string {
    if (str) {
      str = replaceWithSmartTextFunction(str);
    }
    return str;
  }

  export function getSmartTextList(): Array<string> {
    // Currently, preferred name is not supported so lets filter it out.
    return smartTextDefinitions.filter((item) => item.key !== '<preferred name>').map((item) => item.key);
  }

  /**
   * Resolves text
   * @param data - the information to use in placeholders represented by smart tags.
   * @param text - string that contains smart tags to be replaced with actual information
   * @param [util] - the i18n utility
   * @returns the text with the smart tags replaced with actual information.
   */
  export function resolveText(data: SmartTextData, text: string, util: I18nUtil = i18n): string {
    if (text) {
      for (let smartTextDefinition of smartTextDefinitions) {
        let replacementValue = smartTextDefinition.getString(data, util);
        text = text.replace(smartTextDefinition.regEx, replacementValue);
      }
    }
    return text;
  }

  /**
   * Generates smart text
   * @param comment - the string to replace words that matches the information from relatedCommentData.
   * @param relatedCommentData - the information to use for searching the comment with and replace with the associated smart tags.
   * @param [util] - the i18n utility
   * @param [stringSmartTextReplacer] - function to replace words that matches the information from relatedCommentData returning the resulting string with smart tags.
   * @returns comment with smart tags
   */
  export function generateSmartText(comment: string | undefined, relatedCommentData: SmartTextData, util: I18nUtil = i18n, stringSmartTextReplacer: StringSmartTextReplacer = defaultStringSmartTextReplacer): string | undefined {
    if (comment) {
      // Set up the replace function to use by the StringSmartTextReplacer function.
      let replaceWithSmartTextFunction = (str: string): string => {
        if (str) {
          str = generateSmartTextFromSimpleString(str, relatedCommentData, util);
        }
        return str;
      };

      // generate the comment with smart tags
      comment = stringSmartTextReplacer(comment, replaceWithSmartTextFunction);
    }
    return comment;
  }
}
